import { useEffect, useContext, useCallback, useState } from 'react';
import { trackEvent, TrackingContext } from '@gjensidige/core-analytics';
import type { TrackEvent } from '@gjensidige/core-analytics';
import type { TrackingProp } from './types';
import helpStyles from './help-text/help-text.module.less';
import validationStyles from './validation-message/validation-message.module.less';
import { name, version } from '../package.json';

declare global {
  interface Window {
    buildersUUID?: number;
  }
}

export const IS_BROWSER =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.navigator !== 'undefined';

export const IS_ANDROID =
  IS_BROWSER && /android/i.test(window.navigator.userAgent); // Bad, but needed

export const IS_IOS =
  IS_BROWSER && /iPad|iPhone|iPod/.test(window.navigator.userAgent); // Bad, but needed

// useId is a new hook in React 18, and we need to support 16.8 and upwards
// In this polyfill, always return temp_id on first render to avoid server/client-hyration mismatch
// and on DOM mount we return a new ID, causing a re-render, but only once, and only to change the ID
// (instead of doing a full re-hydration as we otherwise would have).
// We also build UUID based on a global variable, to avoid conflictin IDs
export function useId() {
  const [id, setId] = useState<undefined | string>(() => undefined);
  useEffect(() => {
    setId(`:b${(window.buildersUUID = (window.buildersUUID || 0) + 1)}`);
  }, []);
  return id;
}

export function useReqireLabel<T>(ref: React.RefObject<T>) {
  useEffect(() => {
    // Let react render first and not block thread
    window.requestAnimationFrame(() => {
      if (ref.current instanceof HTMLElement) {
        const self = ref.current;
        const label =
          self.closest('label') ||
          (self.id && document.querySelector(`label[for="${self.id}"]`));

        if (!label)
          console.warn(
            'Form element missing <Label>. Please set an id={exampleId} and corresponding <Label htmlFor={exampleId}>:',
            self
          );
      }
    });
  }, [ref]);
}

export function useTracking(tracking: TrackingProp) {
  const { options } = useContext(TrackingContext);

  return useCallback(
    (dynamic?: TrackEvent) => {
      if (tracking)
        trackEvent(
          {
            eventVersion: version,
            eventSource: name,
            eventLabel: '',
            ...dynamic,
            ...(tracking === true ? {} : tracking)
          },
          options
        );
    },
    [tracking, options]
  );
}

export function getText(el: unknown) {
  if (!(el instanceof HTMLElement)) return '';
  return (el.innerText || el.textContent || el.getAttribute('aria-label') || '')
    .replace(/\n/g, ' ')
    .trim();
}

export function getLabel(el: unknown) {
  if (el instanceof HTMLInputElement) el = el.labels?.[0];
  if (!(el instanceof HTMLElement)) return '';
  const help = getText(el.querySelector(helpStyles.helpText));
  const validation = getText(el.querySelector(validationStyles.message));
  return getText(el).replace(help, '').replace(validation, ''); // As label is the wrapping element, remove inner texts
}

export function onRoleButtonKeyDown(event: React.KeyboardEvent<Element>) {
  if (event.key === ' ' || event.key === 'Enter') {
    const click = new MouseEvent('click', { bubbles: true, cancelable: true });
    event.preventDefault(); // Prevent scroll
    event.currentTarget.dispatchEvent(click); // Using .dispatchEvent as SVG elements does not support .click
  }
}
